/**
 * SurveillanceDefensivePanel — Onglet défensif dans Veille.
 *
 * Affiche les signaux d'exposition publique détectés sur le domaine et les
 * contacts watchlist. Tavily-only, mode 100% gratuit.
 *
 * Accessible : Ultra Admin + Admin.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { useTranslation } from "react-i18next";
import { Shield, RefreshCw, Loader2, AlertTriangle, ChevronDown, ChevronUp, Clock, ExternalLink } from "lucide-react";

const CRIT_COLORS = { critique: "#E24B4A", modere: "#EF9F27", faible: "#A1A1AA" };
const TYPE_LABELS = {
  credentials_leak: "Fuite credentials",
  data_exposure: "Données exposées",
  sensitive_doc: "Document sensible",
  risky_mention: "Mention à risque",
  other: "Autre",
};

export default function SurveillanceDefensivePanel() {
  const { t } = useTranslation();
  const [data, setData] = useState({ signals: [], summary: "", scanned_at: null, domain: null });
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const [expandedIdx, setExpandedIdx] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get(`/workspace/scraping/defensive/list`);
      setData(r.data || { signals: [], summary: "" });
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const triggerScan = async (forceRefresh = false) => {
    setBusy(true);
    setProgress("Démarrage…");
    try {
      const startRes = await api.post(`/workspace/scraping/defensive/scan/start`, { force_refresh: forceRefresh });
      if (startRes.data?.from_cache && !forceRefresh) {
        await refresh(); setBusy(false); return;
      }
      const jobId = startRes.data?.job_id;
      if (!jobId) throw new Error("no job_id");
      for (let i = 0; i < 45; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        const poll = await api.get(`/workspace/scraping/defensive/scan/jobs/${jobId}`);
        setProgress(poll.data?.progress || "");
        if (poll.data?.status === "done") {
          await refresh();
          notify.success(t("saas.defensive.done", { defaultValue: "Surveillance défensive terminée." }));
          setBusy(false); return;
        }
        if (poll.data?.status === "failed") throw new Error(poll.data?.error || "job failed");
      }
      throw new Error("timeout");
    } catch (e) {
      notify.error(fmtError(e, "Scan défensif échoué"));
    } finally {
      setBusy(false); setProgress("");
    }
  };

  const signals = data.signals || [];

  return (
    <div className="noam-card p-5 space-y-4" data-testid="defensive-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="noam-overline inline-flex items-center gap-1.5">
            <Shield className="w-3 h-3 text-[#E24B4A]" />
            {t("saas.defensive.overline", { defaultValue: "Surveillance défensive · mode gratuit Tavily" })}
          </div>
          <h3 className="text-lg font-semibold text-white mt-1">
            {t("saas.defensive.title", { defaultValue: "Signaux d'exposition publique" })}
          </h3>
          {data.domain && (
            <div className="text-[11px] text-zinc-500 mt-1">
              {t("saas.defensive.domain", { defaultValue: "Domaine surveillé" })} : <span className="text-zinc-300">{data.domain}</span>
            </div>
          )}
          {data.scanned_at && (
            <div className="text-[11px] text-zinc-500 mt-0.5 inline-flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {new Date(data.scanned_at).toLocaleString()}
            </div>
          )}
        </div>
        <button onClick={() => triggerScan(true)} disabled={busy} className="btn-primary text-xs" data-testid="defensive-scan-btn">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          {busy ? (progress || t("saas.defensive.scanning", { defaultValue: "Analyse…" })) : t("saas.defensive.scan", { defaultValue: "Lancer un scan" })}
        </button>
      </div>

      {data.summary && (
        <div className="bg-[#E24B4A]/5 border border-[#E24B4A]/20 rounded-lg p-3 text-xs text-zinc-200 leading-relaxed whitespace-pre-wrap" data-testid="defensive-summary">
          {data.summary}
        </div>
      )}

      {signals.length === 0 && !busy && (
        <div className="text-center py-8" data-testid="defensive-empty">
          <Shield className="w-10 h-10 text-zinc-600 mx-auto opacity-40" />
          <div className="text-sm text-zinc-300 mt-3">
            {t("saas.defensive.empty_title", { defaultValue: "Aucun signal d'exposition détecté" })}
          </div>
          <p className="text-[11px] text-zinc-500 mt-1 max-w-md mx-auto">
            {t("saas.defensive.empty_help", { defaultValue: "Lancez un scan pour surveiller votre domaine et vos contacts watchlist via Tavily deep search." })}
          </p>
        </div>
      )}

      <div className="space-y-2">
        {signals.map((s, idx) => {
          const isOpen = expandedIdx === idx;
          const critCol = CRIT_COLORS[s.criticality] || "#A1A1AA";
          return (
            <div key={idx} className="bg-white/[0.02] border border-white/5 rounded-lg p-3" data-testid={`defensive-signal-${idx}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap mb-1">
                    <AlertTriangle className="w-3 h-3" style={{ color: critCol }} />
                    <span className="text-[10px] px-1.5 py-0.5 rounded border" style={{ borderColor: `${critCol}40`, color: critCol }}>
                      {s.criticality || "?"}
                    </span>
                    <span className="text-[10px] text-zinc-500">{TYPE_LABELS[s.type] || s.type || "—"}</span>
                  </div>
                  <h4 className="text-sm text-white font-medium leading-snug">{s.title}</h4>
                </div>
                <button onClick={() => setExpandedIdx(isOpen ? null : idx)} className="btn-ghost text-[10px] shrink-0" data-testid={`defensive-toggle-${idx}`}>
                  {isOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </button>
              </div>
              {isOpen && (
                <div className="mt-3 pt-3 border-t border-white/5 space-y-2.5">
                  {s.evidence && (
                    <div className="text-[12px] text-zinc-300 leading-relaxed">{s.evidence}</div>
                  )}
                  {s.recommended_action && (
                    <div className="bg-[#1D9E75]/5 border border-[#1D9E75]/20 rounded-md p-2.5">
                      <div className="text-[9px] uppercase tracking-wider text-[#1D9E75] mb-1">
                        {t("saas.defensive.action", { defaultValue: "Action recommandée" })}
                      </div>
                      <div className="text-[11px] text-zinc-200">{s.recommended_action}</div>
                    </div>
                  )}
                  {s.source_url && (
                    <a href={s.source_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-[#378ADD] hover:text-white inline-flex items-center gap-1 truncate">
                      <ExternalLink className="w-2.5 h-2.5" />
                      {s.source_url}
                    </a>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
